import { RuleValidationStatus, type RuleValidationTargetType } from '@atlas/types'
import { RuleValidationResults } from '@atlas/ui-domain'
import { Button } from '@atlas/ui-primitives'
import { toRuleValidationRows } from '@/adapters/rule-validation-row'
import { useRequestRuleValidation, useRuleValidationRun, useRuleValidationRuns } from '@/hooks/useRuleValidation'
import { notification } from '@/notifications/notification-store'

/** What the header says while a run is still answering. Nothing here is a verdict yet. */
const RUNNING_LABEL: Partial<Record<RuleValidationStatus, string>> = {
	[RuleValidationStatus.REQUESTED]: 'Starting',
	[RuleValidationStatus.SENT_TO_VALIDATOR]: 'Running',
	[RuleValidationStatus.FAILED]: 'Run failed to start',
}

export interface RuleValidationPanelProps {
	targetType: RuleValidationTargetType
	targetId: string
	title?: string
	/** Group the rules under the asset they judged. For a target holding many of them. */
	groupBySubject?: boolean
}

/**
 * The validate action and the latest run's per-rule results, on every surface that offers
 * them.
 *
 * It always shows the LATEST run: a run is immutable and re-validating creates a new one, so
 * a surface showing anything else would be showing stale evidence next to a button that
 * produces fresh evidence.
 */
export function RuleValidationPanel({ targetType, targetId, title, groupBySubject }: RuleValidationPanelProps) {
	const { data: runs = [], isPending: runsLoading, isError: runsFailed } = useRuleValidationRuns(targetType, targetId)
	const latest = runs[0] ?? null
	const {
		data: run,
		isPending: runLoading,
		isError: runFailed,
	} = useRuleValidationRun(targetType, targetId, latest?.requestId ?? null)
	const request = useRequestRuleValidation(targetType, targetId)

	const onValidate = () =>
		request.mutate(undefined, {
			onSuccess: () => notification.success('Validation requested — verdicts appear as each rule is answered.'),
			onError: () => notification.error('Could not request validation. Please try again.'),
		})

	return (
		<RuleValidationResults
			title={title}
			rows={toRuleValidationRows(run?.results ?? [])}
			runDate={latest?.createdAt ?? null}
			runStatus={latest ? RUNNING_LABEL[latest.status] : undefined}
			// From the DETAIL query, the same one `rows` comes from: read off the list row
			// instead, the two go out of step every time the detail is still in flight and an
			// empty table gets reported as a finished run that resolved nothing.
			isRunComplete={run?.status === RuleValidationStatus.COMPLETED}
			isLoading={runsLoading || (!!latest && runLoading)}
			isError={runsFailed || runFailed}
			groupBySubject={groupBySubject}
			action={
				<Button size="sm" variant="secondary" isDisabled={request.isPending} onPress={onValidate}>
					{request.isPending ? 'Requesting…' : latest ? 'Re-validate' : 'Validate'}
				</Button>
			}
		/>
	)
}
